import ErrorMessage from "@/v2-ui/ErrorMessage"
import Separator from "@/v2-ui/separator"
import cn from "@/v2-ui/utils/utils.cn"
import FeatureAccessSection from "@/v2-backoffice-client/feature/access/FeatureAccessSection"
import FeatureAccessSkeleton from "@/v2-backoffice-client/feature/access/FeatureAccessSkeleton"
import FeatureCountrySection from "@/v2-backoffice-client/feature/country/FeatureCountrySection"
import { getCountryEmptyMeaning } from "@/v2-backoffice-client/feature/country/feature.country.utils"
import FeatureUnpublishedNotice from "@/v2-backoffice-client/feature/FeatureUnpublishedNotice"
import { useFeatureQuery } from "@/v2-backoffice-client/feature/feature.query"

export type FeatureAvailabilityTabProps = {
  className?: string
  featureCode: string
  /** Names the thing in the copy, e.g. "basemap". */
  subject: string
  /** Surface segment of this tab's feature codes, e.g. "Basemaps". */
  featureCodeLabel: string
}

/**
 * Who gets this feature — the one question, answered by the two gates that
 * decide it. Who has it at all is read here and decided on Licensing >
 * Feature access (the personal-data flag beside it is saved here); the country
 * list narrows where it is offered.
 *
 * The saves here land in the running environment immediately and mirror to
 * the authoring catalog, unlike the text on the same entry, which waits for
 * a translation publish.
 *
 * The two sections load independently: the frame is here from the first
 * frame and each fills itself in, so a slow country list cannot hold up the
 * availability and neither can move the other's heading down the page.
 *
 * What an empty country list means is derived from the feature's type once
 * the feature row has loaded (map layers and basemaps: hidden everywhere;
 * anything else: offered everywhere). Map layers have their own tab
 * (LayerAvailabilityTab) that adds the publish switch and the categories.
 */
function FeatureAvailabilityTab(props: FeatureAvailabilityTabProps) {
  const { featureCode, subject, featureCodeLabel, className } = props

  const { data, loading, error } = useFeatureQuery({
    variables: { featureCode },
  })
  const feature = data?.getFeature

  const emptyMeans = getCountryEmptyMeaning(feature?.FeatureTypeCode)

  return (
    <div className={cn("flex flex-col gap-4 p-3", className)}>
      <section className="flex flex-col gap-2">
        <header className="text-sm font-medium">Access</header>
        {loading ? (
          <FeatureAccessSkeleton />
        ) : feature ? (
          <FeatureAccessSection
            feature={feature}
            featureCodeLabel={featureCodeLabel}
          />
        ) : error ? (
          // A failed read is not "unpublished": say what happened instead.
          <ErrorMessage error={error} />
        ) : (
          <FeatureUnpublishedNotice subject={subject} />
        )}
      </section>

      <Separator />

      <section className="flex flex-col gap-2">
        <header className="text-sm font-medium">Countries</header>
        <FeatureCountrySection
          featureCode={featureCode}
          subject={subject}
          featureCodeLabel={featureCodeLabel}
          emptyMeans={emptyMeans}
        />
      </section>
    </div>
  )
}

export default FeatureAvailabilityTab
